import { useCallback, useEffect, useRef, useState } from 'react';
import { ImageKind } from 'shared/utils/image/upscale/constants';
import { UpscaleImageError } from 'shared/utils/image/upscale/upscale-error';
import { Upscaler, UpscaleState } from './types';

/**
 * `upscaleImage` подгружается динамическим `import()` по первому вызову.
 *
 * Статический импорт протащил бы в каждый потребитель кита `create-upscale-worker.ts` с
 * `import.meta` — Jest 27 его не разбирает, и упал бы любой тест формы с дропзоной. Заодно
 * код апскейла не попадает в основной бандл: всё тяжёлое грузится только по кнопке
 * (`.claude/plans/image-ai-upscale.md`, «Ленивая загрузка»).
 */
const loadAndUpscale: Upscaler = async (file, options) => {
  const { upscaleImage } = await import('shared/utils/image/upscale/upscale-image');
  return upscaleImage(file, options);
};

interface UseImageUpscaleParams {
  file: File;
  kind: ImageKind;
  upscaler?: Upscaler;
}

interface UseImageUpscaleResult {
  state: UpscaleState;
  /** Запустить заново после ошибки. */
  retry: () => void;
  /** Оборвать обработку — модалка зовёт при закрытии. */
  abort: () => void;
}

/**
 * Состояние модалки улучшения: исходник → загрузка модели → обработка → результат / ошибка.
 *
 * Улучшение стартует при монтировании: модалку открывает клик «Улучшить», и это единственный
 * момент, когда модель имеет право начать грузиться. Размонтирование обрывает воркер и
 * отзывает объектный адрес результата.
 */
export function useImageUpscale({ file, kind, upscaler = loadAndUpscale }: UseImageUpscaleParams): UseImageUpscaleResult {
  const [state, setState] = useState<UpscaleState>({ status: 'loading-model' });
  const [attempt, setAttempt] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);
  // Апскейлер читается из ref: новая стрелка от родителя на каждом рендере не должна перезапускать модель.
  const upscalerRef = useRef(upscaler);
  upscalerRef.current = upscaler;

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    let resultUrl: string | null = null;

    setState({ status: 'loading-model' });

    upscalerRef
      .current(file, {
        kind,
        signal: controller.signal,
        onProgress: ({ done, total, backend }) => {
          if (!controller.signal.aborted) setState({ status: 'processing', done, total, backend });
        },
      })
      .then((result) => {
        if (controller.signal.aborted) return;
        resultUrl = URL.createObjectURL(result);
        setState({ status: 'done', file: result, url: resultUrl });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // Не догрузился сам чанк апскейла (`import()`) — для человека это та же «модель не загрузилась».
        const reason = error instanceof UpscaleImageError ? error.reason : 'model-load';
        setState({ status: 'error', reason: reason === 'aborted' ? 'inference' : reason });
      });

    return () => {
      controller.abort();
      if (resultUrl) URL.revokeObjectURL(resultUrl);
    };
  }, [file, kind, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const abort = useCallback(() => controllerRef.current?.abort(), []);

  return { state, retry, abort };
}
